import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, type DataEndpointRateLimit } from "@/lib/api";
import {
  buildRateLimitPatch,
  formatEffectiveRateLimitMessage,
  formValuesFromRateLimit,
  normalizeDataEndpointRateLimit,
  rateLimitStatusLabel,
} from "@/lib/endpointRateLimit";
import { ProgressRing } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type Props = {
  projectId: string;
  table: string;
  onError?: (err: unknown) => void;
};

export function EndpointRateLimitPanel({ projectId, table, onError }: Props) {
  const [loaded, setLoaded] = useState<DataEndpointRateLimit | null>(null);
  const [publicRPM, setPublicRPM] = useState("");
  const [serviceRPM, setServiceRPM] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const status = useMemo(() => rateLimitStatusLabel(loaded), [loaded]);

  async function reload() {
    if (!projectId || !table) return;
    setLoading(true);
    try {
      const res = await api.getDataEndpointRateLimit(projectId, table);
      const data = normalizeDataEndpointRateLimit(res.data, table);
      setLoaded(data);
      const vals = formValuesFromRateLimit(data);
      setPublicRPM(vals.publicRPM);
      setServiceRPM(vals.serviceRPM);
      setFieldError(null);
    } catch (e) {
      onError?.(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, table]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setFieldError(null);
    const patch = buildRateLimitPatch(publicRPM, serviceRPM, loaded);
    if (!patch.ok) {
      setFieldError(patch.error);
      return;
    }
    if (patch.noChange) {
      setMessage("No changes to save.");
      return;
    }
    // Clearing both → confirm destructive full reset via delete path.
    const clearingAll =
      patch.body.public_rpm === null &&
      patch.body.service_rpm === null &&
      Object.keys(patch.body).length === 2;
    if (clearingAll) {
      if (
        !confirm(
          `Reset rate limits for "${table}" to global gateway defaults? This removes all custom limits for this table.`,
        )
      ) {
        return;
      }
    }
    setSaving(true);
    try {
      const res = await api.setDataEndpointRateLimit(projectId, table, patch.body);
      const data = normalizeDataEndpointRateLimit(res.data, table);
      setLoaded(data);
      const vals = formValuesFromRateLimit(data);
      setPublicRPM(vals.publicRPM);
      setServiceRPM(vals.serviceRPM);
      setMessage(formatEffectiveRateLimitMessage(data));
    } catch (err) {
      onError?.(err);
    } finally {
      setSaving(false);
    }
  }

  async function onResetDefaults() {
    setMessage(null);
    setFieldError(null);
    if (loaded?.using_defaults) {
      setMessage("Already using global defaults.");
      return;
    }
    if (
      !confirm(
        `Reset rate limits for "${table}" to global defaults?\n\nPublic → ${loaded?.default_public_rpm ?? 120}/min\nService → ${loaded?.default_service_rpm ?? 600}/min\n\nThis cannot be undone except by saving new values.`,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.deleteDataEndpointRateLimit(projectId, table);
      await reload();
      setMessage("Reset to global gateway defaults.");
    } catch (err) {
      // If already defaults, GET-after may be cleaner; still surface errors.
      onError?.(err);
    } finally {
      setSaving(false);
    }
  }

  const defPub = loaded?.default_public_rpm ?? 120;
  const defSvc = loaded?.default_service_rpm ?? 600;
  const effPub = loaded?.effective_public_rpm ?? defPub;
  const effSvc = loaded?.effective_service_rpm ?? defSvc;

  return (
    <form
      onSubmit={onSave}
      className="card space-y-3 p-5"
      data-testid="endpoint-rate-limit-settings"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-sans text-sm font-semibold text-text-primary">
            Advanced rate limit
          </h2>
          <p className="mt-1 text-xs text-text-muted">
            Controls{" "}
            <code className="font-mono">/v1/data/{table || ":table"}</code> only.
            Empty fields use the gateway default. Saving one field keeps the
            other unchanged.
          </p>
        </div>
        <Badge
          variant={status.tone === "custom" ? "default" : "secondary"}
          data-testid="endpoint-rate-limit-status"
        >
          {loading ? (
            <span className="inline-flex items-center gap-1.5">
              <ProgressRing size="sm" label="Loading rate limits" />
              Loading…
            </span>
          ) : (
            status.label
          )}
        </Badge>
      </div>

      <div
        className="rounded-md border border-border-standard/80 bg-surface-container/40 px-3 py-2 text-xs"
        data-testid="endpoint-rate-limit-effective"
      >
        <div className="font-medium text-text-primary">Currently enforced</div>
        <div className="mt-1 grid gap-1 font-mono text-text-secondary sm:grid-cols-2">
          <span>
            Public key:{" "}
            <strong className="text-text-primary">{effPub}</strong> req/min
            {!loaded?.public_overridden ? (
              <span className="text-text-disabled"> (default)</span>
            ) : null}
          </span>
          <span>
            Service key:{" "}
            <strong className="text-text-primary">{effSvc}</strong> req/min
            {!loaded?.service_overridden ? (
              <span className="text-text-disabled"> (default)</span>
            ) : null}
          </span>
        </div>
        {loaded?.updated_at && !loaded.using_defaults ? (
          <div className="mt-1 text-text-disabled">
            Last updated {new Date(loaded.updated_at).toLocaleString()}
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="public-rpm">
            Public key RPM
          </label>
          <input
            id="public-rpm"
            className="input font-mono"
            inputMode="numeric"
            placeholder={`Default ${defPub}`}
            value={publicRPM}
            onChange={(e) => setPublicRPM(e.target.value)}
            disabled={loading || saving}
            aria-invalid={!!fieldError}
          />
          <p className="mt-1 text-[11px] text-text-disabled">
            Browser / public key traffic · default {defPub}/min
          </p>
        </div>
        <div>
          <label className="label" htmlFor="service-rpm">
            Service key RPM
          </label>
          <input
            id="service-rpm"
            className="input font-mono"
            inputMode="numeric"
            placeholder={`Default ${defSvc}`}
            value={serviceRPM}
            onChange={(e) => setServiceRPM(e.target.value)}
            disabled={loading || saving}
          />
          <p className="mt-1 text-[11px] text-text-disabled">
            Server-side service key · default {defSvc}/min
          </p>
        </div>
      </div>

      {fieldError ? (
        <p className="text-xs text-destructive" role="alert">
          {fieldError}
        </p>
      ) : null}
      {message ? (
        <p className="text-xs text-secondary" data-testid="endpoint-rate-limit-message">
          {message}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={loading || saving} size="sm">
          {saving ? "Saving…" : "Save rate limit"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || saving || !!loaded?.using_defaults}
          onClick={() => void onResetDefaults()}
          data-testid="endpoint-rate-limit-reset"
        >
          Reset to defaults
        </Button>
      </div>
    </form>
  );
}
